/**
 * Reusable animation primitives powered by Framer Motion.
 *
 * Includes scroll-triggered fade-ins, staggered children reveals,
 * decorative floating orbs, glassmorphism surfaces, gradient-border
 * cards, animated counters, and a page-transition wrapper.
 *
 * @module AnimatedComponents
 */

import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Box, BoxProps, alpha } from '@mui/material';
import { GREEN, DARK_BLUE } from '../theme';

/** Props for the scroll-triggered fade-in wrapper. */
interface FadeInSectionProps {
  children: React.ReactNode;
  direction?: 'up' | 'down' | 'left' | 'right';
  delay?: number;
  duration?: number;
  once?: boolean;
}

/**
 * Fades children into view when they enter the viewport.
 * Supports sliding from four directions with configurable delay and duration.
 */
export function FadeInSection({
  children,
  direction = 'up',
  delay = 0,
  duration = 0.7,
  once = true,
}: FadeInSectionProps) {
  const { ref, inView } = useInView({
    triggerOnce: once,
    threshold: 0.1,
  });

  const offsets = {
    up: { y: 50, x: 0 },
    down: { y: -50, x: 0 },
    left: { y: 0, x: -50 },
    right: { y: 0, x: 50 },
  };

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, ...offsets[direction] }}
      animate={inView ? { opacity: 1, y: 0, x: 0 } : {}}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.46, 0.45, 0.94],
      }}
    >
      {children}
    </motion.div>
  );
}

/** Props for the stagger container that reveals children one by one. */
interface StaggerContainerProps {
  children: React.ReactNode;
  staggerDelay?: number;
}

/** Container that staggers the entrance animation of its children. */
export function StaggerContainer({ children, staggerDelay = 0.1 }: StaggerContainerProps) {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={{
        visible: {
          transition: { staggerChildren: staggerDelay },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/** Individual child item inside a StaggerContainer — slides up and fades in. */
export function StaggerItem({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) {
  return (
    <motion.div
      style={style}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: {
          opacity: 1,
          y: 0,
          transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
        },
      }}
    >
      {children}
    </motion.div>
  );
}

/** Props for the decorative floating orb background element. */
interface FloatingOrbProps extends BoxProps {
  size?: number;
  color?: string;
  top?: string | number;
  left?: string | number;
  right?: string | number;
  bottom?: string | number;
  animationDelay?: string;
}

/** Decorative blurred circle that floats with a gentle vertical animation. */
export function FloatingOrb({
  size = 300,
  color = GREEN,
  top,
  left,
  right,
  bottom,
  animationDelay = '0s',
  ...rest
}: FloatingOrbProps) {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle, ${alpha(color, 0.15)} 0%, transparent 70%)`,
        filter: 'blur(40px)',
        animation: 'float 8s ease-in-out infinite',
        animationDelay,
        pointerEvents: 'none',
        top,
        left,
        right,
        bottom,
        zIndex: 0,
      }}
      {...rest}
    />
  );
}

/** Props for the glassmorphism paper surface. */
interface GlassPaperProps extends BoxProps {
  children: React.ReactNode;
  intensity?: 'light' | 'medium' | 'strong';
}

/** Semi-transparent surface with backdrop blur — a "frosted glass" effect. */
export function GlassPaper({ children, intensity = 'medium', sx, ...rest }: GlassPaperProps) {
  const intensityMap = {
    light: { bg: 'rgba(255,255,255,0.5)', blur: 10 },
    medium: { bg: 'rgba(255,255,255,0.7)', blur: 20 },
    strong: { bg: 'rgba(255,255,255,0.85)', blur: 30 },
  };
  const { bg, blur } = intensityMap[intensity];

  return (
    <Box
      sx={{
        background: bg,
        backdropFilter: `blur(${blur}px)`,
        WebkitBackdropFilter: `blur(${blur}px)`,
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 4,
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/** Card wrapper with an animated gradient border (green ↔ dark-blue). */
export function GradientBorderCard({ children, sx, ...rest }: BoxProps) {
  return (
    <Box
      sx={{
        position: 'relative',
        borderRadius: '20px',
        p: '2px',
        height: '100%',
        background: `linear-gradient(135deg, ${alpha(GREEN, 0.4)}, ${alpha(DARK_BLUE, 0.4)}, ${alpha(GREEN, 0.4)})`,
        backgroundSize: '200% 200%',
        animation: 'gradient-shift 6s ease infinite',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-8px)',
          background: `linear-gradient(135deg, ${alpha(GREEN, 0.7)}, ${alpha(DARK_BLUE, 0.7)}, ${alpha(GREEN, 0.7)})`,
          backgroundSize: '200% 200%',
          animation: 'gradient-shift 3s ease infinite',
        },
        ...sx,
      }}
      {...rest}
    >
      <Box
        sx={{
          background: 'white',
          borderRadius: '18px',
          height: '100%',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

/** Props for the scroll-triggered animated counter. */
interface AnimatedNumberProps {
  value: number;
  suffix?: string;
  prefix?: string;
}

/** Displays a number that counts up from zero when it enters the viewport. */
export function AnimatedNumber({ value, suffix = '', prefix = '' }: AnimatedNumberProps) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.3 });

  return (
    <span ref={ref}>
      {inView ? (
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        >
          {prefix}
          <CountUpDisplay to={value} />
          {suffix}
        </motion.span>
      ) : (
        `${prefix}0${suffix}`
      )}
    </span>
  );
}

/** Internal helper that animates a counter from 0 to the target value over 2 seconds. */
function CountUpDisplay({ to }: { to: number }) {
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = to / steps;
    let current = 0;
    const timer = setInterval(() => {
      current += increment;
      if (current >= to) {
        setCount(to);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, duration / steps);
    return () => clearInterval(timer);
  }, [to]);

  return <>{count}</>;
}

/** Wraps child content in a fade-up entrance transition for page-level navigation. */
export function PageTransition({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
    >
      {children}
    </motion.div>
  );
}
